import { combineLatestWith, switchMap, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Params } from '@angular/router';
import { SubscriptionDisposable } from '../SubscriptionDisposable';
import { isInternetExplorer } from '../utils/browser.check';
import { isNullOrUndefined } from '../utils/object.extensions';
import { of } from 'rxjs';
import { inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { MatSnackBar } from '@angular/material/snack-bar';

export class BaseComponent extends SubscriptionDisposable {
  public noChangeUrlWhenRedirect = false;
  public isIE = false;
  public hubName: string;
  public allRouteParams: Params;

  ngRxStore = inject(Store<IAppState>);
  frontendSettings: IFrontendSettings;
  snackBar = inject(MatSnackBar);

  constructor(public componentOptions: BaseComponentOption = new BaseComponentOption()) {
    super();
    this.isIE = isInternetExplorer();

    if (componentOptions.activatedRoute) {
      let allParams = componentOptions.activatedRoute.params;
      let parent = componentOptions.activatedRoute.parent;
      let child = componentOptions.activatedRoute.firstChild;
      while (!isNullOrUndefined(child)) {
        allParams = allParams.pipe(
          combineLatestWith(child.params),
          switchMap(([a, b]) => {
            return of(Object.assign({}, a, b));
          })
        );
        child = child.firstChild;
      }

      while (!isNullOrUndefined(parent)) {
        allParams = allParams.pipe(
          combineLatestWith(parent.params),
          switchMap(([a, b]) => {
            return of(Object.assign({}, a, b));
          })
        );
        parent = parent.parent;
      }

      allParams.pipe(takeUntil(this.isDestroyed)).subscribe((params) => {
        this.allRouteParams = params;
      });

      this.hubName = this.allRouteParams['hubName'];

      // In embedded/script mode the browser URL bar stays at the host's hub-less path
      // (e.g. /trip-planner) because the app is bootstrapped with skipLocationChange.
      // On browser back/forward Angular can re-resolve that URL and capture a feature
      // path (e.g. "trip-planner") as :hubName. The wrapper's `route` attribute is the
      // authoritative source of the real hub, so prefer it when embedded.
      if (this.isScriptSolution) {
        this.hubName = this.getClientName() || this.hubName;
      }
    }

    this.noChangeUrlWhenRedirect = this.isScriptSolution;

    this.ngRxStore
      .pipe(takeUntil(this.isDestroyed), select(frontendSettingsSelector))
      .subscribe((settings) => {
        settings && (this.frontendSettings = settings);
      });
  }

  public getParam(paramName: string) {
    return this.componentOptions.activatedRoute.snapshot.paramMap.get(paramName);
  }

  public getScriptValue(valueName: string) {
    return (
      this.imgoingWrapper?.attributes[valueName] ||
      this.imgoingWrapper?.attributes[`data-${valueName}`]
    )?.value;
  }

  public get isScriptSolution() {
    return !!this.imgoingWrapper;
  }

  public get imgoingWrapper() {
    return (
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix')
    );
  }

  public get shadowRoot() {
    return (this.imgoingWrapper || document).querySelector('app-root')?.shadowRoot || null;
  }

  public get aiBuddyShadowRoot() {
    const aiBuddyElement = document.querySelector('#ig-ai-buddy');
    return aiBuddyElement ? aiBuddyElement.shadowRoot : null;
  }

  getClientName() {
    if (!this.imgoingWrapper) return null;

    let route =
      this.imgoingWrapper.attributes['route'] || this.imgoingWrapper.attributes['data-route'];
    if (route) {
      const url = route.value;
      const noSlash = url.startsWith('/') ? url.substr(1) : url;
      const clientName = noSlash.substr(0, noSlash.indexOf('/'));
      return clientName;
    }
    return null;
  }

  showSuccess(msg: string, duration: number = 3000) {
    this.snackBar.open(msg, 'OK', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['ig-success-snackbar'],
      duration: duration,
    });
  }

  showError(msg: string, duration: number = 5000) {
    this.snackBar.open(msg, 'Close', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['ig-error-snackbar'],
      duration: duration,
    });
  }

  closeSnackBar() {
    this.snackBar.dismiss();
  }
}

export class BaseComponentOption {
  activatedRoute?: ActivatedRoute;
}
